import classNames from "classnames";
import React, {
	ComponentPropsWithRef,
	forwardRef,
	PropsWithChildren,
	useImperativeHandle,
	useState,
} from "react";

import "./dialog.css";
import { Icon, IconName } from "../icon";
import { IconButton } from "../icon-button";
import { TransparentButton } from "../transparent-button";

export const DialogTitle = ({ children }: PropsWithChildren) => (
	<div className="dialog__title">{children}</div>
);
DialogTitle.displayName = "DialogTitle";

export const DialogCloseIcon = ({ onClick }: { onClick?: () => void }) =>
	onClick && (
		<div className="dialog__close-icon">
			<IconButton className="dialog__close-button" iconName="cross" onClick={onClick} />
		</div>
	);
DialogCloseIcon.displayName = "DialogCloseIcon";

export const DialogContent = ({
	children,
	html,
}: PropsWithChildren<{
	html?: string;
}>) => {
	if (html) {
		return <div className="dialog__content" dangerouslySetInnerHTML={{ __html: html }} />;
	}
	return <div className="dialog__content">{children}</div>;
};
DialogContent.displayName = "DialogContent";

export const DialogFooter = ({ children }: PropsWithChildren) => (
	<div className="dialog__footer">{children}</div>
);
DialogFooter.displayName = "DialogFooter";

const DialogContainer = forwardRef<HTMLDivElement, PropsWithChildren>(({ children }, ref) => (
	<div className="dialog-container" ref={ref}>
		{children}
	</div>
));
DialogContainer.displayName = "DialogContainer";

export const Dialog = forwardRef<
	HTMLDivElement,
	PropsWithChildren<{
		className?: string;
		icon?: IconName;
		fullScreen?: boolean;
		onCloseIcon?(): void;
	}>
>(({ children, className, icon, fullScreen, onCloseIcon }, ref) => {
	const cssClass = classNames([
		"dialog",
		className,
		{
			"dialog--full-screen": fullScreen,
		},
	]);

	return (
		<DialogContainer ref={ref}>
			<div className={cssClass}>
				{!!icon && (
					<div className="dialog__left">
						<Icon iconName={icon} />
					</div>
				)}
				<div className="dialog__right">
					{!!onCloseIcon && <DialogCloseIcon onClick={onCloseIcon} />}
					{children}
				</div>
			</div>
		</DialogContainer>
	);
});
Dialog.displayName = "Dialog";

export interface DialogButtonHandle {
	toggleDisable: (force?: boolean) => void;
}
export const DialogButton = forwardRef<DialogButtonHandle, ComponentPropsWithRef<"button">>(
	({ children, disabled, ...props }, ref) => {
		const [isDisabled, setIsDisabled] = useState(disabled);

		useImperativeHandle(ref, () => ({
			toggleDisable: (force?: boolean) => {
				setIsDisabled(force !== undefined ? force : !isDisabled);
			},
		}));

		const { className, ...rest } = props;
		const cssClass = classNames(["dialog__button", className]);

		return (
			<TransparentButton type="button" className={cssClass} disabled={isDisabled} {...rest}>
				{children}
			</TransparentButton>
		);
	},
);
DialogButton.displayName = "DialogButton";
